/**
 * RejectedScreen — the two terminal account states.
 *
 * REJECTED (the application was refused) and SUSPENDED (an approved account was
 * switched off) are both blocked at JwtStrategy rather than at OnboardingGuard,
 * so every authenticated request 401s and there is nothing either account can
 * do in the product. One screen covers both because the merchant's options are
 * identical — contact support, or sign out — and only the explanation differs.
 *
 * It deliberately does not poll. Both states are reversed by a human at
 * BIGXSTAR, and a spinner implying otherwise would be a lie.
 */
import { useAppStore } from '@/store/useAppStore';
import { OnboardingCard, PRIMARY, QUIET } from './OnboardingCard';

const SUPPORT_EMAIL = 'support@bigxbook.app';

export function RejectedScreen({ onLogout }: { onLogout: () => void }) {
  const currentUser = useAppStore((s) => s.currentUser);
  const suspended = currentUser?.onboardingStatus === 'SUSPENDED';

  const heading = suspended ? 'Account suspended' : 'Registration not approved';
  const summary = suspended
    ? 'This account has been switched off and cannot bill until it is restored.'
    : 'Your business registration was reviewed and could not be approved.';
  const detail = suspended
    ? 'A suspension is usually a billing or compliance hold. Support can tell you which, and lift it.'
    : 'This is usually a detail we could not verify — a mismatched GSTIN or business name. Support can re-open the review.';

  const subject = encodeURIComponent(
    suspended ? 'BigxBook account suspended' : 'BigxBook registration review',
  );
  const body = encodeURIComponent(
    `${suspended ? 'My account has been suspended.' : 'My registration was not approved.'}\n\n` +
      `Phone: ${currentUser?.phone ?? '—'}\n` +
      `Merchant ID: ${currentUser?.merchantId ?? '—'}\n\nPlease review.`,
  );

  return (
    <OnboardingCard
      title={heading}
      subtitle={summary}
      footer={
        <button type="button" onClick={onLogout} className={`${QUIET} w-full`}>
          Sign out and use a different number
        </button>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-body text-fg-secondary">{detail}</p>

        {currentUser?.merchantId && (
          <div className="rounded-lg border border-white/10 bg-white/[0.06] p-3 backdrop-blur-sm">
            <p className="text-label uppercase text-fg-tertiary">Merchant ID</p>
            <p className="mt-0.5 break-all font-mono text-label text-fg-secondary">
              {currentUser.merchantId}
            </p>
          </div>
        )}

        <a
          href={`mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`}
          className={`${PRIMARY} block text-center no-underline`}
        >
          Contact support
        </a>
      </div>
    </OnboardingCard>
  );
}
